/**
 * Bronconfigurator (US-011 t/m US-015, design-handoff scherm 7).
 *
 * De beheerder kiest een product, plakt één of meer concurrent-URL's, en richt per bron in hoe
 * de juiste variant wordt gekozen en waar de prijs staat. Dat gebeurt door op een schermafdruk
 * van de échte pagina te klikken; er hoeft geen selectorcode te worden geschreven.
 *
 * De pagina kan niet in een iframe: webshops verbieden dat vrijwel altijd. De server laadt hem
 * daarom met een headless browser en levert een afdruk plus de posities van de bruikbare
 * elementen. Na iedere stap wordt opnieuw geïnspecteerd, zodat zichtbaar is wat de keuze deed.
 */

import {
  formatEuro,
  SOURCE_STATUS_LABELS,
  VAT_MODE_LABELS,
  type CrawlStep,
  type SourceSelectors,
  type VatMode,
} from '@prijsmonitor/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ApiError, api } from '../api/client.js';
import { Button, Card, Chip, Notice, ui } from '../components/ui/index.js';
import styles from './ConfiguratorScreen.module.css';

interface Product {
  id: string;
  name: string;
  ownSku: string;
  lengthLabel: string | null;
  color: string | null;
  colorCritical: boolean;
  foil: string | null;
  foilCritical: boolean;
}

interface Source {
  id: string;
  sharedSourceId: string | null;
  sharedRevision: number;
  name: string;
  url: string | null;
  domain: string | null;
  kind: 'OWN_FEED' | 'OWN_SHOP' | 'COMPETITOR';
  status: 'DRAFT' | 'TESTED' | 'ACTIVE' | 'PAUSED' | 'ARCHIVED';
  competitorSku: string | null;
  steps: CrawlStep[];
  selectors: SourceSelectors;
  vatMode: VatMode;
  vatRatePercent: number;
  robotsWarning: string | null;
  needsRetest: boolean;
  lastTestOk: boolean;
  lastTestMessage: string | null;
}

interface InspectedElement {
  selector: string;
  /** Het element dat een klik werkelijk aanneemt, wanneer dat een ander is dan het aangewezen. */
  clickSelector?: string;
  kind: 'SELECT' | 'CLICKABLE' | 'TEXT';
  tag: string;
  text: string;
  options?: Array<{ value: string; label: string }>;
  /**
   * Een keuzelijst die in de pagina staat maar niet zichtbaar is.
   *
   * Bij webshops met kleur- en maatknopjes is dát de échte besturing; de knopjes zetten hem alleen
   * maar. Hij staat op de plek van het blok waar de keuze hoort, zodat hij aanwijsbaar is.
   */
  hidden?: boolean;
  rect: { x: number; y: number; width: number; height: number };
  looksLikePrice: boolean;
  /**
   * Een doorgestreept bedrag: de oude adviesprijs.
   *
   * Die val is niet alleen voor de AI gezet. Wie hem met de hand aanwijst, legt een bron vast die
   * elke nacht netjes een geldig bedrag meet dat gewoon te hoog is, en daar is in geen enkel scherm
   * iets aan te zien. Daarom krijgt hij hier niet de opmaak die zegt "dit is een prijs", en staat het
   * in de tekst die je bij het aanwijzen ziet.
   */
  doorgestreept?: boolean;
}

/** Voorstel uit de AI-zoekopdracht, na verificatie door onze eigen crawler (US-036/037). */
interface Kandidaat {
  url: string;
  winkel: string;
  motivatie: string;
  /** Zolang de crawler deze pagina nog niet heeft geopend. */
  bezig: boolean;
  bereikbaar: boolean;
  probleem: string | null;
  titel: string | null;
  beoordeling: {
    score: number;
    aanbevolen: boolean;
    voorbehoud: string | null;
    bevindingen: Array<{
      kenmerk: string;
      status: 'MATCH' | 'AFWIJKING' | 'ONBEKEND';
      toelichting: string;
    }>;
  } | null;
}

/** De stand van een zoekopdracht op de server. */
interface ZoekTaak {
  zoekId: string;
  status: 'ZOEKEN' | 'VERIFIEREN' | 'KLAAR' | 'MISLUKT';
  stap: string;
  kandidaten: Kandidaat[];
  fout: string | null;
}

/** De stand van een pagina-analyse op de server (US-013). */
interface AnalyseTaak {
  analyseId: string;
  /** Bij welke bron deze analyse hoort; het paneel toont alleen die van de gekozen bron. */
  sourceId: string;
  bronNaam: string;
  status: 'WACHTRIJ' | 'INSPECTEREN' | 'DENKEN' | 'TESTEN' | 'KLAAR' | 'MISLUKT';
  stap: string;
  fout: string | null;
  uitkomst: {
    paginaSoort: 'ENKELE_VARIANT' | 'KEUZE_NODIG' | 'NIET_GESCHIKT';
    stappen: string[];
    prijsTekst: string | null;
    prijsCents: number | null;
    lengteTekst: string | null;
    kleurTekst: string | null;
    btwBehandeling: VatMode;
    zekerheid: number;
    toelichting: string;
    testGeslaagd: boolean;
    testMelding: string | null;
    testLog: Array<{ ok: boolean; message: string }>;
    pogingen: number;
  } | null;
}

interface Inspection {
  url: string;
  title: string;
  screenshot: string;
  viewport: { width: number; height: number };
  elements: InspectedElement[];
  log: Array<{ ok: boolean; message: string }>;
}

interface TestResult {
  ok: boolean;
  status: string;
  message: string | null;
  priceRaw: string | null;
  log: Array<{ ok: boolean; message: string }>;
  durationMs: number;
  source: Source;
}

/** De rollen die een aangeklikt element kan krijgen. */
const ROLLEN: Array<{ key: keyof SourceSelectors; label: string; hint: string }> = [
  { key: 'price', label: 'Prijs', hint: 'Het element met het bedrag' },
  { key: 'lengthConfirm', label: 'Lengte', hint: 'Waar de gekozen lengte te lezen is' },
  { key: 'colorConfirm', label: 'Kleur', hint: 'Waar de gekozen kleur te lezen is' },
  { key: 'foilConfirm', label: 'Folie', hint: 'Waar de folie-uitvoering te lezen is' },
  { key: 'skuConfirm', label: 'Artikelnummer', hint: 'Het artikelnummer van de concurrent' },
  {
    key: 'vatConfirm',
    label: 'Btw-vermelding',
    hint: 'Optioneel — alleen nodig wanneer de btw op de pagina moet worden bevestigd',
  },
];

const STATUS_TONE: Record<Source['status'], 'muted' | 'info' | 'success' | 'warn'> = {
  DRAFT: 'muted',
  TESTED: 'info',
  ACTIVE: 'success',
  PAUSED: 'warn',
  ARCHIVED: 'muted',
};

function stepText(step: CrawlStep): string {
  switch (step.type) {
    case 'ACCEPT_COOKIES':
      return `Cookies accepteren — ${step.label ?? step.selector ?? ''}`;
    case 'CLICK':
      return `Klikken op ${step.label ?? step.selector ?? ''}`;
    case 'SELECT_OPTION':
      return `${step.label ?? 'Keuzelijst'} instellen op "${step.value ?? ''}"`;
    case 'WAIT':
      return `Wachten ${step.value ?? 1000} ms`;
    default:
      return step.type;
  }
}

export function ConfiguratorScreen(): React.JSX.Element {
  const [params, setParams] = useSearchParams();

  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState(params.get('product') ?? '');
  const [sources, setSources] = useState<Source[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /**
   * De bron uit een binnenkomende link, bv. vanuit het foutoverzicht in Taken & logboek.
   *
   * Alleen bij de eerste keer bronnen laden toepassen: `setParams({ product: productId })` hieronder
   * verwijst de URL naar alleen het product, dus na die eerste keer is er niets meer om op terug te
   * vallen — en dat is ook precies gewenst, anders zou wisselen van bron in dit scherm zelf steeds
   * worden overschreven door de link waarmee je binnenkwam.
   */
  const initialSourceIdRef = useRef(params.get('source'));

  const [urlInput, setUrlInput] = useState('');
  /** Het product waarvan de prijsbronnen in één keer worden overgenomen. */
  const [kopieVan, setKopieVan] = useState('');
  const [aiEnabled, setAiEnabled] = useState(false);
  const [kandidaten, setKandidaten] = useState<Kandidaat[] | null>(null);
  /** De lopende of laatst afgeronde zoekopdracht op de server. */
  const [zoekId, setZoekId] = useState<string | null>(null);
  const [zoekTaak, setZoekTaak] = useState<ZoekTaak | null>(null);
  /**
   * Alle analyses van dit product, niet één losse.
   *
   * Er stond hier één taak, en dat gaf twee problemen tegelijk. De uitkomst bleef in beeld nadat er
   * op een andere bron was geklikt — je las dan het oordeel over de vorige pagina bij de nieuwe. En
   * wie wegnavigeerde raakte de taak kwijt, waardoor het leek alsof de analyse stopte terwijl hij
   * op de server gewoon doorliep. Met een lijst per product hoort elke uitkomst bij zijn eigen bron
   * en is die na terugkomen weer op te halen.
   */
  const [analyses, setAnalyses] = useState<AnalyseTaak[]>([]);
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [pendingRole, setPendingRole] = useState<keyof SourceSelectors | null>(null);

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const shotRef = useRef<HTMLImageElement>(null);
  const [shotWidth, setShotWidth] = useState(0);

  const selected = sources.find((s) => s.id === selectedId) ?? null;
  const product = products.find((p) => p.id === productId) ?? null;
  /** De analyse van de gekozen bron; bij een andere bron verdwijnt het paneel vanzelf. */
  const analyse = analyses.find((taak) => taak.sourceId === selectedId) ?? null;
  const analysesBezig = analyses.filter(
    (taak) => taak.status !== 'KLAAR' && taak.status !== 'MISLUKT',
  );

  // --- Gegevens laden ------------------------------------------------------

  useEffect(() => {
    /*
     * Alleen producten die actief worden gemonitord.
     *
     * Een concept is nog niet af en een gearchiveerd product wordt niet meer gemeten; prijsbronnen
     * inrichten heeft daar geen zin, en in een lijst van tientallen producten maakt het het zoeken
     * onnodig lastig. Staat er een ander product in het adres — bijvoorbeeld uit een oude link —
     * dan wordt dat vervangen door het eerste dat er wél in staat, want anders wijst het scherm
     * naar iets wat niet in de lijst voorkomt.
     */
    api
      .get<{ products: Product[] }>('/api/products?status=ACTIVE')
      .then((r) => {
        setProducts(r.products);
        setProductId((huidig) =>
          huidig && r.products.some((p) => p.id === huidig) ? huidig : (r.products[0]?.id ?? ''),
        );
      })
      .catch((e: ApiError) => setError(e.message));

    // Zonder AI-sleutel blijft de zoekknop weg; de configurator werkt verder ongewijzigd.
    api
      .get<{ aiEnabled: boolean }>('/api/config')
      .then((r) => setAiEnabled(r.aiEnabled))
      .catch(() => setAiEnabled(false));
  }, []);

  const loadSources = useCallback(async (id: string) => {
    if (!id) return;
    const response = await api.get<{ sources: Source[] }>(`/api/products/${id}/sources`);
    // Ook de eigen webshoppagina hoort hier: dat is een gewone webshop die geconfigureerd en
    // getest moet worden (US-009). Alleen de feedbron valt af — daar is niets aan te wijzen.
    const competitors = response.sources.filter((s) => s.kind !== 'OWN_FEED');
    setSources(competitors);

    const uitLink = initialSourceIdRef.current;
    initialSourceIdRef.current = null;
    setSelectedId((huidig) => {
      if (uitLink && competitors.some((s) => s.id === uitLink)) return uitLink;
      return huidig && competitors.some((s) => s.id === huidig)
        ? huidig
        : (competitors[0]?.id ?? null);
    });
  }, []);

  useEffect(() => {
    if (!productId) return;
    setParams({ product: productId }, { replace: true });
    setInspection(null);
    setTestResult(null);
    setKandidaten(null);
    setZoekId(null);
    setZoekTaak(null);
    setAnalyses([]);
    void loadSources(productId).catch((e: ApiError) => setError(e.message));

    // Wat er voor dit product loopt of net klaar is weer ophalen. Zonder dit leek een analyse te
    // stoppen zodra je het scherm verliet; op de server liep hij door en kwam de uitkomst nergens
    // meer terecht. Stil bij een fout: dit is aanvullende informatie, geen storing om te melden.
    api
      .get<{ taken: AnalyseTaak[] }>(`/api/products/${productId}/analyses`)
      .then((r) => setAnalyses(r.taken))
      .catch(() => undefined);
  }, [productId, loadSources, setParams]);

  /**
   * Haalt de stand van de zoekopdracht op zolang die loopt. De server doet het werk; hier wordt
   * alleen gekeken hoe ver hij is, zodat er geen enkel verzoek lang openstaat.
   */
  useEffect(() => {
    if (!zoekId) return;

    let gestopt = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const ophalen = async (): Promise<void> => {
      if (gestopt) return;
      try {
        const taak = await api.get<ZoekTaak>(`/api/kandidaten/${zoekId}`);
        if (gestopt) return;

        setZoekTaak(taak);
        setKandidaten(taak.kandidaten.length > 0 ? taak.kandidaten : null);
        if (taak.status === 'MISLUKT' && taak.fout) setError(taak.fout);

        if (taak.status === 'ZOEKEN' || taak.status === 'VERIFIEREN') {
          timer = setTimeout(() => void ophalen(), 2_000);
        }
      } catch (e) {
        if (!gestopt) setError(e instanceof ApiError ? e.message : 'De stand is niet op te halen.');
      }
    };

    void ophalen();

    // Bij een productwissel of het verlaten van het scherm stopt het ophalen; de taak op de
    // server loopt door en is later gewoon weer op te vragen.
    return () => {
      gestopt = true;
      if (timer) clearTimeout(timer);
    };
  }, [zoekId]);

  /**
   * Haalt de stand van de analyses op zolang er nog iets loopt (US-013).
   *
   * Zelfde opzet als bij het zoeken naar kandidaten: de server doet het werk, het scherm kijkt
   * alleen hoe ver hij is. Inspecteren, nadenken en testen duren samen te lang voor één verzoek.
   * Het ophalen gaat per product en niet per taak, zodat een wachtrij van zes bronnen niet zes
   * losse afspraken oplevert die elk hun eigen einde moeten bewaken.
   */
  const aantalBezig = analysesBezig.length;
  useEffect(() => {
    if (!productId || aantalBezig === 0) return;

    let gestopt = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const ophalen = async (): Promise<void> => {
      if (gestopt) return;
      try {
        const { taken } = await api.get<{ taken: AnalyseTaak[] }>(
          `/api/products/${productId}/analyses`,
        );
        if (gestopt) return;

        setAnalyses(taken);
        const nogBezig = taken.some((t) => t.status !== 'KLAAR' && t.status !== 'MISLUKT');
        if (nogBezig) {
          timer = setTimeout(() => void ophalen(), 2_000);
          return;
        }

        // Alles klaar: de bronnen zijn onderweg gewijzigd — stappen, selectors en de teststand.
        await loadSources(productId);
        setInspection(null);
      } catch (e) {
        if (!gestopt) setError(e instanceof ApiError ? e.message : 'De stand is niet op te halen.');
      }
    };

    timer = setTimeout(() => void ophalen(), 2_000);

    // Bij het verlaten van het scherm stopt alleen het ophalen; de wachtrij op de server loopt door
    // en is bij terugkomst gewoon weer op te vragen.
    return () => {
      gestopt = true;
      if (timer) clearTimeout(timer);
    };
  }, [productId, aantalBezig, loadSources]);

  async function run<T>(key: string, action: () => Promise<T>): Promise<T | null> {
    setBusy(key);
    setError(null);
    try {
      return await action();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Er ging iets mis. Probeer het opnieuw.');
      return null;
    } finally {
      setBusy(null);
    }
  }

  // --- Acties --------------------------------------------------------------

  async function addUrls(): Promise<void> {
    const urls = urlInput
      .split('\n')
      .map((u) => u.trim())
      .filter(Boolean);
    if (urls.length === 0) return;

    const result = await run('add', () =>
      api.post<{
        added: number;
        duplicates: number;
        invalid: number;
        results: Array<{ overgenomenVan?: string }>;
      }>(`/api/products/${productId}/sources`, { urls }),
    );
    if (!result) return;

    setUrlInput('');
    const delen = [`${result.added} toegevoegd`];
    if (result.duplicates) delen.push(`${result.duplicates} al gekoppeld`);
    if (result.invalid) delen.push(`${result.invalid} ongeldig`);

    // Was deze webshop al eens ingericht, dan begint de bron met die instellingen. Dat expliciet
    // melden, want anders lijkt er zonder aanleiding al iets ingevuld te staan.
    const overgenomen = result.results.filter((r) => r.overgenomenVan).length;
    if (overgenomen > 0) {
      delen.push(
        `${overgenomen} met de instellingen van dezelfde webshop bij een ander product — ` +
          'controleer de lengtekeuze en test',
      );
    }
    setNotice(delen.join(' · '));
    await loadSources(productId);
  }

  /**
   * Start het zoeken naar kandidaatpagina's (US-036).
   *
   * De server doet het werk op de achtergrond; hier wordt alleen de stand opgehaald. Het
   * resultaat is een voorstel: er wordt niets aangemaakt totdat een URL wordt overgenomen.
   */
  async function zoekKandidaten(): Promise<void> {
    if (!productId) return;
    setKandidaten(null);

    const taak = await run('kandidaten', () =>
      api.post<ZoekTaak>(`/api/products/${productId}/kandidaten`),
    );
    if (taak) setZoekId(taak.zoekId);
  }

  /**
   * Laat de AI de pagina analyseren en het voorstel meteen testen (US-013).
   *
   * De uitkomst is nooit een actieve bron: de beheerder controleert of de juiste prijs is gelezen en
   * activeert zelf. Mislukt de analyse, dan blijft handmatig inrichten gewoon beschikbaar.
   */
  async function analyseerPagina(): Promise<void> {
    if (!selected) return;
    setTestResult(null);

    const taak = await run('analyseren', () =>
      api.post<AnalyseTaak>(`/api/sources/${selected.id}/analyseren`),
    );
    if (taak)
      setAnalyses((huidig) => [taak, ...huidig.filter((t) => t.sourceId !== taak.sourceId)]);
  }

  /**
   * Alle bronnen van dit product achter elkaar laten analyseren (US-013).
   *
   * De server werkt ze één voor één af en gaat door wanneer dit scherm wordt verlaten. Dat was de
   * reden voor deze knop: bij een product met zes concurrenten stond de beheerder een half uur te
   * wachten om telkens de volgende te kunnen starten.
   */
  async function analyseerAlleBronnen(): Promise<void> {
    if (!productId) return;
    setTestResult(null);

    const uitkomst = await run('analyseren-alle', () =>
      api.post<{ taken: AnalyseTaak[] }>(`/api/products/${productId}/analyseren`),
    );
    if (!uitkomst) return;
    setAnalyses(uitkomst.taken);
    setNotice(
      `${uitkomst.taken.length} bron(nen) in de wachtrij. Ze worden één voor één geanalyseerd; ` +
        'u kunt dit scherm intussen verlaten.',
    );
  }

  async function inspect(steps?: CrawlStep[]): Promise<void> {
    if (!selected) return;
    const result = await run('inspect', () =>
      api.post<Inspection>(`/api/sources/${selected.id}/inspect`, {
        steps: steps ?? selected.steps,
      }),
    );
    if (result) setInspection(result);
  }

  async function saveConfig(patch: Partial<Source>): Promise<void> {
    if (!selected) return;
    const updated = await run('save', () =>
      api.patch<Source>(`/api/sources/${selected.id}`, {
        steps: patch.steps ?? selected.steps,
        selectors: patch.selectors ?? selected.selectors,
        ...(patch.competitorSku !== undefined ? { competitorSku: patch.competitorSku } : {}),
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.vatMode !== undefined ? { vatMode: patch.vatMode } : {}),
        ...(patch.vatRatePercent !== undefined ? { vatRatePercent: patch.vatRatePercent } : {}),
      }),
    );
    if (updated) setSources((list) => list.map((s) => (s.id === updated.id ? updated : s)));
  }

  /**
   * Neemt in één keer alle concurrentbronnen van een ander product over.
   *
   * Veel producten verschillen alleen in lengte en worden bij dezelfde concurrenten verkocht, die
   * doorgaans alle lengtes voeren. Ze komen binnen als concept: per bron moet de lengtekeuze nog
   * kloppen, en bij webshops met een eigen pagina per lengte ook het adres.
   */
  async function neemBronnenOver(): Promise<void> {
    if (!productId || !kopieVan) return;

    const resultaat = await run('bronnen-overnemen', () =>
      api.post<{ melding: string }>(`/api/products/${productId}/bronnen-overnemen`, {
        vanProductId: kopieVan,
      }),
    );
    if (!resultaat) return;

    setKopieVan('');
    setNotice(resultaat.melding);
    await loadSources(productId);
  }

  /**
   * Neemt de instellingen over van een eerder ingerichte bron op hetzelfde domein.
   *
   * Voor veel producten zijn het dezelfde concurrenten. Bij het toevoegen van een URL gebeurt dit
   * automatisch; deze knop is er voor bronnen die er al stonden.
   */
  async function neemOver(): Promise<void> {
    if (!selected) return;
    const resultaat = await run('overnemen', () =>
      api.post<{ source: Source; melding: string }>(`/api/sources/${selected.id}/overnemen`),
    );
    if (!resultaat) return;

    setSources((list) => list.map((s) => (s.id === resultaat.source.id ? resultaat.source : s)));
    setNotice(resultaat.melding);
    setInspection(null);
    setTestResult(null);
  }

  /** Verwerkt een klik op de schermafdruk. */
  async function onElementClick(element: InspectedElement): Promise<void> {
    if (!selected) return;

    // Een rol toewijzen heeft voorrang: de beheerder gaf aan wat dit element betekent.
    if (pendingRole) {
      await saveConfig({ selectors: { ...selected.selectors, [pendingRole]: element.selector } });
      setPendingRole(null);
      return;
    }

    // Anders wordt het een interactiestap.
    if (element.kind === 'SELECT') {
      const keuzes = element.options ?? [];
      const waarde = window.prompt(
        `Welke waarde moet worden gekozen?\n\n${keuzes.map((o) => `• ${o.label} (${o.value})`).join('\n')}`,
        keuzes[1]?.value ?? '',
      );
      if (waarde === null) return;
      const step: CrawlStep = {
        type: 'SELECT_OPTION',
        selector: element.selector,
        value: waarde,
        label: element.text.slice(0, 60) || 'Keuzelijst',
      };
      const steps = [...selected.steps, step];
      await saveConfig({ steps });
      await inspect(steps);
      return;
    }

    const isCookie = /cookie|akkoord|accepteer|accept/i.test(element.text);
    const step: CrawlStep = {
      type: isCookie ? 'ACCEPT_COOKIES' : 'CLICK',
      // Klikken gaat naar het element dat de klik aanneemt. Bij een maatkeuze staat de tekst vaak
      // in een element met pointer-events:none binnen een label; die tekst vastleggen liet de
      // klik eindeloos opnieuw proberen.
      selector: element.clickSelector ?? element.selector,
      label: element.text.slice(0, 60) || element.tag,
    };
    const steps = [...selected.steps, step];
    await saveConfig({ steps });
    await inspect(steps);
  }

  /**
   * Voegt een wachtstap toe.
   *
   * Het enige gereedschap dat de beheerder zelf heeft voor een pagina die traag opbouwt. De
   * applicatie wacht al tot de pagina niet meer verandert, maar dat is niet altijd genoeg.
   */
  async function voegWachtstapToe(): Promise<void> {
    if (!selected) return;
    const antwoord = window.prompt('Hoeveel seconden wachten voordat er verder wordt gegaan?', '5');
    if (antwoord === null) return;

    const seconden = Number(antwoord.replace(',', '.'));
    if (!Number.isFinite(seconden) || seconden <= 0) {
      setError('Geef een aantal seconden op, bijvoorbeeld 5.');
      return;
    }

    const ms = Math.min(Math.round(seconden * 1000), 15_000);
    const steps = [...selected.steps, { type: 'WAIT' as const, value: String(ms) }];
    await saveConfig({ steps });
    await inspect(steps);
  }

  /**
   * Downloadt de opgebouwde HTML van de pagina.
   *
   * Voor het geval de configurator iets anders toont dan een gewone browser. Een leeg vlak op de
   * afdruk vertelt niet of een blok ontbreekt, leeg is of ergens anders is beland; de HTML wél.
   */
  async function haalPaginabron(): Promise<void> {
    if (!selected) return;
    setBusy('paginabron');
    setError(null);
    try {
      const response = await fetch(`/api/sources/${selected.id}/paginabron`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ steps: selected.steps }),
      });
      if (!response.ok) throw new Error('De paginabron kon niet worden opgehaald.');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selected.domain ?? 'pagina'}.html`;
      link.click();
      URL.revokeObjectURL(url);
      setNotice('De paginabron is gedownload. Dit is wat de crawler op dat moment zag.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'De paginabron kon niet worden opgehaald.');
    } finally {
      setBusy(null);
    }
  }

  async function removeStep(index: number): Promise<void> {
    if (!selected) return;
    const steps = selected.steps.filter((_, i) => i !== index);
    await saveConfig({ steps });
    await inspect(steps);
  }

  async function testSource(): Promise<void> {
    if (!selected) return;
    const result = await run('test', () =>
      api.post<TestResult>(`/api/sources/${selected.id}/test`, {}),
    );
    if (!result) return;
    setTestResult(result);
    setSources((list) => list.map((s) => (s.id === result.source.id ? result.source : s)));
  }

  async function changeStatus(actie: 'activeren' | 'pauzeren'): Promise<void> {
    if (!selected) return;
    const updated = await run(actie, () =>
      api.post<Source>(`/api/sources/${selected.id}/${actie}`),
    );
    if (updated) setSources((list) => list.map((s) => (s.id === updated.id ? updated : s)));
  }

  /**
   * Verwijdert de geselecteerde bron. Zonder metingen verdwijnt hij echt; met metingen
   * archiveert de server hem, zodat de prijsgeschiedenis niet met één klik weg is.
   */
  async function verwijderBron(): Promise<void> {
    if (!selected) return;
    const bevestigd = window.confirm(
      `"${selected.name}" verwijderen als prijsbron van dit product?\n\n` +
        'Zijn er al dagprijzen vastgelegd, dan wordt de bron gearchiveerd en blijft die ' +
        'historie bewaard.',
    );
    if (!bevestigd) return;

    const resultaat = await run('verwijderen', () =>
      api.delete<{ verwijderd: boolean; melding: string }>(`/api/sources/${selected.id}`),
    );
    if (!resultaat) return;

    setNotice(resultaat.melding);
    setSelectedId(null);
    setInspection(null);
    setTestResult(null);
    await loadSources(productId);
  }

  // --- Weergave ------------------------------------------------------------

  const schaal = inspection && shotWidth ? shotWidth / inspection.viewport.width : 1;
  const gekozenSelectors = new Set(Object.values(selected?.selectors ?? {}).filter(Boolean));

  return (
    <div className={ui.page}>
      {error && <Notice tone="error">{error}</Notice>}
      {notice && <Notice tone="success">{notice}</Notice>}

      <div className={styles.layout}>
        <div className={styles.column}>
          {/* Stap 1 — product */}
          <Card>
            <div className={styles.stepLabel}>
              <span className={styles.stepNumber}>1</span> Product
            </div>
            <select
              className={styles.select}
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              aria-label="Product"
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} · {p.ownSku}
                </option>
              ))}
            </select>
            {product && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                <Chip tone="info">Lengte {product.lengthLabel ?? '—'} · kritisch</Chip>
                {product.color && (
                  <Chip tone={product.colorCritical ? 'info' : 'muted'}>
                    Kleur {product.color} · {product.colorCritical ? 'kritisch' : 'niet kritisch'}
                  </Chip>
                )}
                {product.foil && (
                  <Chip tone={product.foilCritical ? 'info' : 'muted'}>
                    Folie {product.foil} · {product.foilCritical ? 'kritisch' : 'niet kritisch'}
                  </Chip>
                )}
              </div>
            )}
          </Card>

          {/* Stap 2 — URL's */}
          <Card>
            <div className={styles.stepLabel}>
              <span className={styles.stepNumber}>2</span> Concurrent-URL&apos;s
            </div>
            <textarea
              className={styles.textarea}
              placeholder="Plak één of meer product-URL's — één per regel"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              aria-label="Concurrent-URL's"
            />
            <div style={{ marginTop: 10 }}>
              <Button
                variant="secondary"
                onClick={() => void addUrls()}
                busy={busy === 'add'}
                disabled={!productId || !urlInput.trim()}
              >
                Toevoegen
              </Button>
            </div>
            <p className={ui.hint} style={{ marginTop: 8 }}>
              Elke regel wordt een eigen bron met status Concept. Dubbele adressen worden
              overgeslagen. Is een webshop al bij een ander product ingericht, dan worden de
              klikstappen, het prijselement en de btw-behandeling meteen overgenomen.
            </p>

            {/* Alle bronnen van een ander product in één keer overnemen. */}
            <div className={styles.overnemen}>
              <label className={ui.hint} htmlFor="bronnen-overnemen">
                Prijsbronnen overnemen van een ander product
              </label>
              <div className={styles.overnemenRij}>
                <select
                  id="bronnen-overnemen"
                  className={styles.select}
                  value={kopieVan}
                  onChange={(e) => setKopieVan(e.target.value)}
                >
                  <option value="">Kies een product…</option>
                  {products
                    .filter((p) => p.id !== productId)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {p.lengthLabel ?? 'geen lengte'} · {p.ownSku}
                      </option>
                    ))}
                </select>
                <Button
                  variant="secondary"
                  onClick={() => void neemBronnenOver()}
                  busy={busy === 'bronnen-overnemen'}
                  disabled={!kopieVan}
                >
                  Neem prijsbronnen over
                </Button>
              </div>
              <p className={ui.hint}>
                Voor een product dat alleen in lengte verschilt en bij dezelfde concurrenten ligt.
                Alles komt binnen als concept: pas per bron de lengtekeuze aan — en bij webshops met
                een eigen pagina per lengte ook het adres — en test ze. Het artikelnummer bij de
                concurrent wordt niet meegenomen, want dat verschilt juist per lengte.
              </p>
            </div>

            {aiEnabled && (
              <div className={styles.kandidaten}>
                <div className={styles.kandidatenKop}>
                  <div>
                    <strong>Kandidaten laten zoeken</strong>
                    <p className={ui.hint}>
                      De AI zoekt op het web naar productpagina&apos;s; daarna opent de crawler elke
                      pagina en legt lengte, kleur en folie naast dit product. Dit duurt een halve
                      minuut.
                    </p>
                  </div>
                  <Button
                    onClick={() => void zoekKandidaten()}
                    busy={
                      busy === 'kandidaten' ||
                      zoekTaak?.status === 'ZOEKEN' ||
                      zoekTaak?.status === 'VERIFIEREN'
                    }
                    disabled={!productId}
                  >
                    Zoeken
                  </Button>
                </div>

                {zoekTaak && zoekTaak.status !== 'MISLUKT' && (
                  <div className={styles.zoekStap}>
                    {(zoekTaak.status === 'ZOEKEN' || zoekTaak.status === 'VERIFIEREN') && (
                      <span className={styles.spinner} aria-hidden="true" />
                    )}
                    {zoekTaak.stap}
                  </div>
                )}

                {kandidaten?.map((kandidaat) => (
                  <div key={kandidaat.url} className={styles.kandidaat}>
                    <div className={styles.kandidaatKop}>
                      <Chip
                        tone={
                          kandidaat.bezig
                            ? 'info'
                            : kandidaat.beoordeling?.aanbevolen
                              ? 'success'
                              : kandidaat.bereikbaar
                                ? 'warn'
                                : 'danger'
                        }
                      >
                        {kandidaat.bezig
                          ? 'controleren…'
                          : kandidaat.beoordeling
                            ? `${kandidaat.beoordeling.score}%${
                                kandidaat.beoordeling.aanbevolen ? ' · aanbevolen' : ''
                              }`
                            : 'niet te openen'}
                      </Chip>
                      <strong>{kandidaat.winkel}</strong>
                      <a href={kandidaat.url} target="_blank" rel="noreferrer noopener">
                        {kandidaat.titel ?? kandidaat.url}
                      </a>
                      <Button
                        variant="secondary"
                        disabled={kandidaat.bezig || !kandidaat.bereikbaar}
                        onClick={() =>
                          setUrlInput((huidig) =>
                            huidig.trim() ? `${huidig.trim()}\n${kandidaat.url}` : kandidaat.url,
                          )
                        }
                      >
                        Overnemen
                      </Button>
                    </div>

                    {kandidaat.probleem && <Notice tone="error">{kandidaat.probleem}</Notice>}

                    {kandidaat.beoordeling && (
                      <ul className={styles.bevindingen}>
                        {kandidaat.beoordeling.bevindingen.map((bevinding) => (
                          <li key={bevinding.kenmerk}>
                            <span
                              className={
                                bevinding.status === 'MATCH'
                                  ? styles.bevindingOk
                                  : styles.bevindingNiet
                              }
                            >
                              {bevinding.status === 'MATCH' ? '✓' : '•'}
                            </span>{' '}
                            <strong>{bevinding.kenmerk}:</strong> {bevinding.toelichting}
                          </li>
                        ))}
                      </ul>
                    )}

                    {kandidaat.beoordeling?.voorbehoud && (
                      <Notice tone="warn">{kandidaat.beoordeling.voorbehoud}</Notice>
                    )}

                    {kandidaat.motivatie && (
                      <p className={ui.hint}>Volgens de AI: {kandidaat.motivatie}</p>
                    )}
                  </div>
                ))}

                {kandidaten && kandidaten.length > 0 && (
                  <p className={ui.hint}>
                    Een voorstel is geen bron. Neem een URL over, configureer hem en test hem — pas
                    dan telt hij mee.
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Stap 3 — bronnen */}
          <Card>
            <div className={styles.stepLabel}>
              <span className={styles.stepNumber}>3</span> Prijsbronnen
            </div>
            {sources.length === 0 ? (
              <p className={ui.hint}>Nog geen prijsbronnen voor dit product.</p>
            ) : (
              <div className={styles.sourceList}>
                {sources.map((source) => (
                  <button
                    key={source.id}
                    type="button"
                    className={source.id === selectedId ? styles.sourceRowActive : styles.sourceRow}
                    onClick={() => {
                      setSelectedId(source.id);
                      setInspection(null);
                      setTestResult(null);
                    }}
                  >
                    <span className={styles.sourceMeta}>
                      <span className={styles.sourceName}>
                        {source.kind === 'OWN_SHOP' ? '★ ' : ''}
                        {source.name}
                      </span>
                      <span className={styles.sourceUrl} style={{ display: 'block' }}>
                        {source.url}
                      </span>
                    </span>
                    <Chip tone={STATUS_TONE[source.status]}>
                      {source.status === 'ACTIVE'
                        ? '✓ Actief'
                        : SOURCE_STATUS_LABELS[source.status]}
                    </Chip>
                  </button>
                ))}
              </div>
            )}
          </Card>

          {selected && (
            <Card
              title="Naam van de bron"
              description="De naam die in het prijsoverzicht en in de e-mails staat."
              actions={
                <Button
                  variant="secondary"
                  onClick={() => void neemOver()}
                  busy={busy === 'overnemen'}
                >
                  Instellingen overnemen
                </Button>
              }
            >
              <input
                key={`naam-${selected.id}`}
                className={styles.textInput}
                defaultValue={selected.name}
                placeholder="bijv. Houthandel Didam"
                onBlur={(e) => {
                  const waarde = e.target.value.trim();
                  if (waarde && waarde !== selected.name) void saveConfig({ name: waarde });
                }}
                aria-label="Naam van de bron"
              />
              <p className={ui.hint} style={{ marginTop: 8 }}>
                De naam wordt uit het adres afgeleid en is niet altijd de winkelnaam. Wijzigen
                verandert niets aan wat er wordt gemeten, dus hertesten is niet nodig.
                <br />
                “Instellingen overnemen” haalt de klikstappen, het prijselement en de
                btw-behandeling op van een al geteste bron van dezelfde webshop bij een ander
                product.
              </p>
            </Card>
          )}

          {selected && (
            <Card title="Btw" description="Wat er met de gelezen prijs gebeurt (US-019).">
              <select
                className={styles.select}
                value={selected.vatMode}
                onChange={(e) => void saveConfig({ vatMode: e.target.value as VatMode })}
                aria-label="Btw-behandeling"
              >
                {(Object.keys(VAT_MODE_LABELS) as VatMode[]).map((modus) => (
                  <option key={modus} value={modus}>
                    {VAT_MODE_LABELS[modus]}
                  </option>
                ))}
              </select>

              {selected.vatMode === 'PRICE_IS_EXCLUSIVE' && (
                <label className={ui.hint} style={{ display: 'block', marginTop: 10 }}>
                  Btw-tarief in procenten
                  <input
                    key={`btw-${selected.id}`}
                    className={styles.textInput}
                    type="number"
                    min={0}
                    max={30}
                    defaultValue={selected.vatRatePercent}
                    onBlur={(e) => {
                      const waarde = Number(e.target.value);
                      if (Number.isFinite(waarde) && waarde !== selected.vatRatePercent) {
                        void saveConfig({ vatRatePercent: waarde });
                      }
                    }}
                  />
                </label>
              )}

              <p className={ui.hint} style={{ marginTop: 8 }}>
                {selected.vatMode === 'ASSUME_INCLUSIVE' &&
                  'Nederlandse consumentenwebshops tonen prijzen inclusief btw; er hoeft niets te ' +
                    'worden aangewezen. Staat er wél een btw-vermelding aangewezen die “exclusief” ' +
                    'zegt, dan wordt er gemeten met een waarneming erbij — een losse regel ' +
                    '“Excl. btw: € 98,91” naast de prijs is immers geen reden om niets te meten.'}
                {selected.vatMode === 'CONFIRM_ON_PAGE' &&
                  'De aangewezen btw-vermelding moet bij iedere controle bevestigen dat de prijs ' +
                    'inclusief btw is. Lukt dat niet, dan blijft de prijs leeg.'}
                {selected.vatMode === 'PRICE_IS_EXCLUSIVE' &&
                  'De prijs op de pagina is exclusief btw en wordt omgerekend, zodat hij ' +
                    'vergelijkbaar blijft met de eigen prijs.'}
              </p>
            </Card>
          )}

          {selected && (
            <Card
              title="Concurrentartikelnummer"
              description="Optioneel. Wordt meegenomen in de variantcontrole (US-007)."
            >
              <input
                key={`sku-${selected.id}`}
                className={styles.textInput}
                defaultValue={selected.competitorSku ?? ''}
                placeholder="bijv. PS-600-WIT"
                onBlur={(e) => {
                  const waarde = e.target.value.trim() || null;
                  if (waarde !== selected.competitorSku) void saveConfig({ competitorSku: waarde });
                }}
                aria-label="Concurrentartikelnummer"
              />
            </Card>
          )}
        </div>

        <div className={styles.column}>
          {selected?.sharedSourceId && (
            <Notice tone="info">
              Gedeelde concurrentbron · configuratieversie {selected.sharedRevision}. Wijzigingen en
              tests gelden voor alle gekoppelde bedrijven. Na een inhoudelijke wijziging is opnieuw
              testen en activeren nodig; eerder actieve deelnemers worden samen hervat. Handmatig
              gepauzeerde deelnemers blijven gepauzeerd.
            </Notice>
          )}
          {selected?.robotsWarning && <Notice tone="warn">{selected.robotsWarning}</Notice>}
          {selected?.needsRetest && (
            <Notice tone="warn">
              De configuratie is gewijzigd na de laatste test. Test de bron opnieuw voordat u hem
              activeert.
            </Notice>
          )}

          {/*
            Uitkomst van de pagina-analyse (US-013).

            Bovenaan, want dit is waar de beheerder naar kijkt: is dit de juiste prijs? Alles
            eronder — de afdruk, de stappen — is de controle daarop.
          */}
          {/*
            De wachtrij, zodra er meer dan één bron in staat. Zonder dit is niet te zien dat er nog
            iets loopt zodra je naar een andere bron kijkt — en dan lijkt het alsof het werk stopt.
          */}
          {analysesBezig.length > 1 && (
            <Card
              title={`${analysesBezig.length} analyses in de wachtrij`}
              description="Ze worden één voor één afgewerkt. U kunt dit scherm verlaten; het werk loopt door op de server."
            >
              <ul className={styles.analyseStappen}>
                {analysesBezig.map((taak) => (
                  <li key={taak.analyseId}>
                    <strong>{taak.bronNaam}</strong> — {taak.stap}
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {analyse && (
            <Card
              title={`Pagina analyseren — ${analyse.bronNaam}`}
              description="De AI stelt een configuratie voor en die wordt meteen getest. Activeren blijft uw keuze."
            >
              {analyse.status !== 'KLAAR' && analyse.status !== 'MISLUKT' && (
                <div className={styles.zoekStap}>
                  <span className={styles.spinner} aria-hidden="true" />
                  {analyse.stap}
                </div>
              )}

              {analyse.status === 'MISLUKT' && <Notice tone="error">{analyse.fout}</Notice>}

              {analyse.uitkomst && (
                <>
                  <div className={styles.analyseKop}>
                    <Chip tone={analyse.uitkomst.testGeslaagd ? 'success' : 'danger'}>
                      {analyse.uitkomst.testGeslaagd ? 'Test geslaagd' : 'Test afgekeurd'}
                    </Chip>
                    <Chip tone={analyse.uitkomst.paginaSoort === 'KEUZE_NODIG' ? 'info' : 'muted'}>
                      {analyse.uitkomst.paginaSoort === 'KEUZE_NODIG'
                        ? 'Lengte moet worden gekozen'
                        : 'Eén vaste variant op deze pagina'}
                    </Chip>
                    <Chip tone={analyse.uitkomst.zekerheid >= 70 ? 'muted' : 'warn'}>
                      Zekerheid {analyse.uitkomst.zekerheid}%
                    </Chip>
                    {analyse.uitkomst.pogingen > 1 && (
                      <Chip tone="warn">Tweede poging na een afgekeurde test</Chip>
                    )}
                  </div>

                  {analyse.uitkomst.testGeslaagd ? (
                    <Notice tone="success">
                      Gelezen prijs: <strong>{analyse.uitkomst.prijsTekst}</strong>
                      {analyse.uitkomst.prijsCents !== null &&
                        ` (${formatEuro(analyse.uitkomst.prijsCents)})`}
                      {analyse.uitkomst.lengteTekst && ` · lengte: ${analyse.uitkomst.lengteTekst}`}
                      {analyse.uitkomst.kleurTekst && ` · kleur: ${analyse.uitkomst.kleurTekst}`}.
                      Klopt dit bedrag met wat er op de pagina staat? Activeer dan de bron.
                    </Notice>
                  ) : (
                    <Notice tone="error">{analyse.uitkomst.testMelding}</Notice>
                  )}

                  <p className={ui.hint}>Volgens de AI: {analyse.uitkomst.toelichting}</p>

                  {analyse.uitkomst.stappen.length > 0 && (
                    <ul className={styles.analyseStappen}>
                      {analyse.uitkomst.stappen.map((stap, index) => (
                        <li key={`${stap}-${index}`}>{stap}</li>
                      ))}
                    </ul>
                  )}

                  <p className={ui.hint}>
                    Btw-behandeling: {VAT_MODE_LABELS[analyse.uitkomst.btwBehandeling]}. De
                    configuratie staat vastgelegd bij de bron; de dagelijkse controle gebruikt hem
                    zoals bij elke andere bron — daar komt geen AI meer aan te pas.
                  </p>
                </>
              )}
            </Card>
          )}

          <div className={styles.browserFrame}>
            <div className={styles.browserBar}>
              <span className={styles.dots} aria-hidden="true">
                <span className={styles.dot} />
                <span className={styles.dot} />
                <span className={styles.dot} />
              </span>
              <span className={styles.urlBar}>
                {inspection?.url ?? selected?.url ?? 'geen bron geselecteerd'}
              </span>
              {aiEnabled && (
                <>
                  <Button
                    onClick={() => void analyseerPagina()}
                    busy={
                      busy === 'analyseren' ||
                      (analyse !== null &&
                        analyse.status !== 'KLAAR' &&
                        analyse.status !== 'MISLUKT')
                    }
                    disabled={!selected}
                  >
                    Pagina analyseren
                  </Button>
                  {/*
                    Alle bronnen achter elkaar. Bij een product met zes concurrenten kostte het
                    telkens opnieuw starten meer aandacht dan het werk zelf.
                  */}
                  <Button
                    variant="secondary"
                    onClick={() => void analyseerAlleBronnen()}
                    busy={busy === 'analyseren-alle'}
                    disabled={sources.length === 0}
                    title="Analyseert alle bronnen van dit product, één voor één, op de achtergrond"
                  >
                    Alle bronnen
                  </Button>
                </>
              )}
              {/*
                Een wachtstap is het enige gereedschap dat de beheerder zelf heeft voor een pagina
                die traag opbouwt. De applicatie wacht al tot de pagina niet meer verandert, maar op
                een webshop met een draaiende carrousel of een langzame achtergrondaanroep is dat
                soms niet genoeg — en dan is "wacht nog vijf seconden" precies de juiste knop.
              */}
              <Button
                variant="secondary"
                onClick={() => void voegWachtstapToe()}
                disabled={!selected}
              >
                + Wachtstap
              </Button>
              <Button
                variant="secondary"
                onClick={() => void haalPaginabron()}
                busy={busy === 'paginabron'}
                disabled={!selected}
              >
                Paginabron
              </Button>
              <Button onClick={() => void inspect()} busy={busy === 'inspect'} disabled={!selected}>
                {inspection ? 'Vernieuwen' : 'Pagina laden'}
              </Button>
            </div>

            {inspection && (
              <div className={styles.assignBar}>
                <span className={styles.assignLabel}>Wijs aan</span>
                {ROLLEN.map((rol) => {
                  const isSet = Boolean(selected?.selectors?.[rol.key]);
                  const actief = pendingRole === rol.key;
                  return (
                    <button
                      key={rol.key}
                      type="button"
                      title={rol.hint}
                      className={isSet ? styles.roleButtonSet : styles.roleButton}
                      style={actief ? { outline: '2px solid var(--c-blue)' } : undefined}
                      onClick={() => setPendingRole(actief ? null : rol.key)}
                    >
                      {isSet ? '✓ ' : ''}
                      {rol.label}
                    </button>
                  );
                })}
                {pendingRole && (
                  <span className={ui.hint}>Klik nu het element aan op de pagina hieronder.</span>
                )}
              </div>
            )}

            <div className={styles.pageArea}>
              {inspection ? (
                <>
                  <img
                    ref={shotRef}
                    src={inspection.screenshot}
                    alt={`Schermafdruk van ${inspection.title}`}
                    className={styles.pageShot}
                    onLoad={(e) => setShotWidth(e.currentTarget.clientWidth)}
                  />
                  {/*
                    Grootste vlakken eerst, kleinste bovenop.

                    Deze knoppen liggen los over de afdruk en stapelen in de volgorde waarin ze
                    staan: wie later komt, ligt erboven en vangt de klik. De lijst zelf staat op
                    bruikbaarheid gesorteerd — prijskandidaten en keuzelijsten vooraan — en dat is
                    precies de verkeerde volgorde om erop te kunnen klikken: die belanden dan
                    ónder de omhullende elementen die er later bij komen. Bij
                    kunststofbouwmateriaal.nl was de lengtekeuze daardoor niet meer aan te klikken.

                    Op oppervlak sorteren maakt het bovendien voorspelbaar: het meest specifieke
                    element dat onder de aanwijzer ligt, is het element dat je krijgt.
                  */}
                  {[...inspection.elements]
                    .sort((a, b) => b.rect.width * b.rect.height - a.rect.width * a.rect.height)
                    .map((element) => {
                      const gekozen = gekozenSelectors.has(element.selector);
                      const className = gekozen
                        ? styles.hotspotChosen
                        : element.looksLikePrice && !element.doorgestreept
                          ? styles.hotspotPrice
                          : styles.hotspot;
                      return (
                        <button
                          key={`${element.kind}-${element.selector}`}
                          type="button"
                          className={className}
                          title={
                            element.doorgestreept
                              ? `${element.tag}: ${element.text} — doorgestreept, dit is de oude prijs`
                              : `${element.tag}: ${element.text || '(geen tekst)'}`
                          }
                          style={{
                            left: element.rect.x * schaal,
                            top: element.rect.y * schaal,
                            width: element.rect.width * schaal,
                            height: element.rect.height * schaal,
                          }}
                          onClick={() => void onElementClick(element)}
                        >
                          <span className="sr-only">{element.text || element.tag} selecteren</span>
                        </button>
                      );
                    })}
                </>
              ) : (
                <p className={styles.placeholder}>
                  {selected
                    ? 'Klik op “Pagina laden” om de productpagina op te halen. Daarna kunt u erop klikken om keuzelijsten en de prijs vast te leggen.'
                    : 'Kies eerst een prijsbron.'}
                </p>
              )}
            </div>
          </div>

          {selected && (
            <Card
              title="Vastgelegde stappen"
              description="Deze stappen worden bij iedere controle in deze volgorde uitgevoerd."
              actions={
                selected.steps.length > 0 ? (
                  <Button
                    onClick={() => {
                      void saveConfig({ steps: [] }).then(() => inspect([]));
                    }}
                    busy={busy === 'save'}
                  >
                    Wissen
                  </Button>
                ) : undefined
              }
            >
              {selected.steps.length === 0 ? (
                <p className={ui.hint}>
                  Nog geen stappen. Klik op de pagina hierboven op een cookiemelding of keuzelijst.
                </p>
              ) : (
                <div className={styles.stepList}>
                  {selected.steps.map((step, index) => (
                    <div key={`${step.type}-${step.selector}-${index}`} className={styles.stepRow}>
                      <span className={styles.stepIndex}>{index + 1}</span>
                      <span className={styles.stepText}>{stepText(step)}</span>
                      <button
                        type="button"
                        className={styles.stepRemove}
                        onClick={() => void removeStep(index)}
                        aria-label={`Stap ${index + 1} verwijderen`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {selected && (
            <Card title="Bron testen">
              <div className={styles.actionRow}>
                <Button
                  variant="secondary"
                  onClick={() => void testSource()}
                  busy={busy === 'test'}
                  disabled={!selected.selectors?.price}
                >
                  Test bron
                </Button>
                <span className={styles.spacer} />
                {selected.status === 'ACTIVE' ? (
                  <Button onClick={() => void changeStatus('pauzeren')} busy={busy === 'pauzeren'}>
                    Pauzeren
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={() => void changeStatus('activeren')}
                    busy={busy === 'activeren'}
                    disabled={!selected.lastTestOk || selected.needsRetest}
                  >
                    Activeren
                  </Button>
                )}
                {/* De eigen webshopbron hangt aan de webshop-URL van het product; die wordt
                    daar beheerd en niet hier weggegooid (US-009). */}
                {selected.kind === 'COMPETITOR' && (
                  <Button
                    variant="danger"
                    onClick={() => void verwijderBron()}
                    busy={busy === 'verwijderen'}
                  >
                    Verwijderen
                  </Button>
                )}
              </div>

              {!selected.selectors?.price && (
                <p className={ui.hint} style={{ marginTop: 8 }}>
                  Wijs eerst het prijselement aan; zonder prijs valt er niets te testen.
                </p>
              )}

              {testResult && (
                <div style={{ marginTop: 'var(--space-12)' }}>
                  <Notice tone={testResult.ok ? 'success' : 'error'}>
                    {testResult.ok
                      ? `Test geslaagd — prijs gevonden: ${testResult.priceRaw}. De bron kan nu worden geactiveerd.`
                      : `Test afgekeurd: ${testResult.message ?? testResult.status}. De bron blijft op concept staan.`}
                  </Notice>
                  <div className={styles.logWindow} style={{ marginTop: 'var(--space-10)' }}>
                    {testResult.log.map((regel, index) => (
                      <div key={index} className={regel.ok ? styles.logOk : styles.logFail}>
                        {regel.message}
                      </div>
                    ))}
                    <div className={styles.logOk}>
                      Klaar in {(testResult.durationMs / 1000).toFixed(1)} s
                    </div>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
